import { stableFingerprint } from "../../lib/command-contract";
import {
  DEFAULT_FLOW_LOOP_LIMIT,
  FLOW_MAX_SUBFLOW_DEPTH,
  FLOW_PLAN_VERSION,
  FLOW_SNAPSHOT_VERSION,
  isFlowControlNode,
  isFlowTerminalNode,
  resolveEffectiveFlowMode,
  type CompiledFlowEdge,
  type CompiledFlowNode,
  type CompiledFlowPlan,
  type FlowCanonicalSnapshot,
  type FlowCondition,
  type FlowDependencyRef,
  type FlowDraft,
  type FlowExecutionMode,
  type FlowNodeConfig,
  type FlowValidationResult,
  type FlowVersionCandidate,
} from "./contracts";
import { flowContentHash } from "./hash";
import { buildFlowGraphIndex, canonicalEdgeOrder, canonicalNodeOrder, cycleClosingEdgeIds, outgoingEdges } from "./graph";
import {
  collectFlowDependencies,
  validateFlowDraft,
  type FlowDependencyCatalog,
  type ValidateFlowDraftOptions,
} from "./validator";

/**
 * Compilador canônico.
 *
 * Ele recebe SOMENTE um rascunho validado e um catálogo autorizado. Não lê
 * relógio, `Math.random`, DOM, rede nem store, e não cria versão publicada:
 * produz um candidato imutável para uma operação transacional futura.
 *
 * O plano executável é neutro de UI: posição visual e rótulo editorial ficam
 * apenas no snapshot. Dois rascunhos semanticamente iguais, com ordem incidental
 * diferente, produzem a mesma serialização e o mesmo hash.
 */

/** Ordena expressões comutativas para que `all`/`any` sejam estáveis. */
export function canonicalizeCondition(condition: FlowCondition): FlowCondition {
  if (condition.type === "comparison") {
    const value = Array.isArray(condition.value)
      ? [...condition.value].sort((left, right) => String(left).localeCompare(String(right)))
      : condition.value;
    return { ...condition, operand: { ...condition.operand }, value };
  }
  if (condition.type === "not") return { type: "not", condition: canonicalizeCondition(condition.condition) };
  const conditions = condition.conditions
    .map(canonicalizeCondition)
    .sort((left, right) => stableFingerprint(left).localeCompare(stableFingerprint(right)));
  return { type: condition.type, conditions };
}

/** Ordena listas internas da configuração sem alterar o significado. */
export function canonicalizeNodeConfig(config: FlowNodeConfig): FlowNodeConfig {
  if (config.type === "set_field_or_tag") {
    return {
      ...config,
      assignments: config.assignments.map((assignment) => ({ ...assignment })).sort((left, right) => {
        if (left.target !== right.target) return left.target.localeCompare(right.target);
        if (left.id !== right.id) return left.id.localeCompare(right.id);
        return left.operation.localeCompare(right.operation);
      }),
    };
  }
  if (config.type === "random_split") {
    return {
      ...config,
      variants: config.variants.map((variant) => ({ ...variant })).sort((left, right) => left.key.localeCompare(right.key)),
    };
  }
  if (config.type === "call_flow") return { ...config, target: { ...config.target } };
  return { ...config };
}

/** Snapshot editorial: preserva o desenho, inclusive posição, para diff futuro. */
export function buildFlowSnapshot(draft: FlowDraft): FlowCanonicalSnapshot {
  return {
    snapshotVersion: FLOW_SNAPSHOT_VERSION,
    flowId: draft.id,
    ownerId: draft.ownerId,
    funnelId: draft.funnelId,
    structureType: draft.structureType,
    name: draft.name,
    description: draft.description,
    mode: draft.mode,
    entryPolicy: draft.entryPolicy,
    entryDomainEvent: draft.entryDomainEvent,
    reentryPolicy: draft.reentryPolicy,
    replyPolicy: draft.replyPolicy,
    isEntryFlow: draft.isEntryFlow,
    nodes: canonicalNodeOrder(draft.nodes).map((node) => ({
      ...node,
      config: canonicalizeNodeConfig(node.config),
      position: node.position ? { ...node.position } : undefined,
    })),
    edges: canonicalEdgeOrder(draft.edges).map((edge) => ({
      ...edge,
      condition: edge.condition ? canonicalizeCondition(edge.condition) : undefined,
    })),
  };
}

export interface CompileFlowOptions extends ValidateFlowDraftOptions {
  /** Modo padrão do funil, primeiro nível da hierarquia funil → flow → passo. */
  funnelMode?: FlowExecutionMode;
}

export interface CompileFlowCandidateOptions extends CompileFlowOptions {
  version: number;
  changelog: string;
  authorId: string;
  /** Instante fornecido pelo chamador; o compilador nunca lê o relógio. */
  createdAt: string;
}

/**
 * Plano executável canônico. Ele NÃO carrega posição, rótulo editorial nem
 * descrição: só o que um runner futuro precisaria para decidir o próximo passo.
 */
export function buildCompiledFlowPlan(
  draft: FlowDraft,
  dependencies: FlowDependencyRef[],
  options: CompileFlowOptions = {},
): CompiledFlowPlan {
  const nodes = canonicalNodeOrder(draft.nodes);
  const edges = canonicalEdgeOrder(draft.edges);
  const index = buildFlowGraphIndex(nodes, edges);
  const startNodeId = nodes.find((node) => node.type === "start")?.id ?? "";
  const closing = startNodeId ? cycleClosingEdgeIds(index, startNodeId) : new Set<string>();
  const flowMode = resolveEffectiveFlowMode({
    funnelMode: options.funnelMode,
    flowMode: draft.mode,
  });

  const compiledNodes: CompiledFlowNode[] = nodes.map((node) => {
    const effective = resolveEffectiveFlowMode({
      funnelMode: options.funnelMode,
      flowMode: draft.mode,
      stepMode: node.mode,
    });
    const outgoing: CompiledFlowEdge[] = outgoingEdges(index, node.id).map((edge) => {
      const closesCycle = closing.has(edge.id);
      return {
        id: edge.id,
        toNodeId: edge.toNodeId,
        port: edge.fromPort,
        priority: edge.priority,
        fallback: edge.fallback,
        variantKey: edge.variantKey,
        condition: edge.condition ? canonicalizeCondition(edge.condition) : undefined,
        // O limite padrão entra no plano para que o runner nunca precise inferi-lo.
        loopLimit: closesCycle ? (edge.loopLimit ?? DEFAULT_FLOW_LOOP_LIMIT) : edge.loopLimit,
        closesCycle,
      };
    });
    return {
      id: node.id,
      type: node.type,
      config: canonicalizeNodeConfig(node.config),
      // A etapa declarada no passo é semântica de execução, não desenho.
      stageId: node.stageId,
      mode: effective.mode,
      modeSource: effective.source,
      executable: !isFlowControlNode(node.type),
      terminal: isFlowTerminalNode(node.type),
      outgoing,
    };
  });

  return {
    planVersion: FLOW_PLAN_VERSION,
    flowId: draft.id,
    ownerId: draft.ownerId,
    funnelId: draft.funnelId,
    structureType: draft.structureType,
    mode: flowMode.mode,
    modeSource: flowMode.source,
    entryPolicy: draft.entryPolicy,
    entryDomainEvent: draft.entryDomainEvent,
    reentryPolicy: draft.reentryPolicy,
    replyPolicy: draft.replyPolicy,
    startNodeId,
    nodes: compiledNodes,
    terminalNodeIds: compiledNodes.filter((node) => node.terminal).map((node) => node.id),
    dependencies,
    maxSubflowDepth: FLOW_MAX_SUBFLOW_DEPTH,
  };
}

/** Serialização canônica do plano; base única do hash e da comparação. */
export function serializeCompiledFlowPlan(plan: CompiledFlowPlan): string {
  return stableFingerprint(plan);
}

export function computeFlowPlanHash(plan: CompiledFlowPlan): string {
  return flowContentHash(serializeCompiledFlowPlan(plan));
}

export interface CompiledFlow {
  snapshot: FlowCanonicalSnapshot;
  plan: CompiledFlowPlan;
  hash: string;
  dependencies: FlowDependencyRef[];
}

export type CompileFlowResult =
  | ({ ok: true; validation: FlowValidationResult } & CompiledFlow)
  | { ok: false; validation: FlowValidationResult };

/**
 * Compila um rascunho. Erros de validação bloqueiam a compilação por inteiro;
 * avisos não bloqueiam e seguem para a revisão humana.
 */
export function compileFlowDraft(
  draft: FlowDraft,
  catalog: FlowDependencyCatalog,
  options: CompileFlowOptions = {},
): CompileFlowResult {
  const validation = validateFlowDraft(draft, catalog, options);
  if (!validation.ok) return { ok: false, validation };
  const dependencies = collectFlowDependencies(draft);
  const snapshot = buildFlowSnapshot(draft);
  const plan = buildCompiledFlowPlan(draft, dependencies, options);
  return { ok: true, validation, snapshot, plan, hash: computeFlowPlanHash(plan), dependencies };
}

export type CompileFlowCandidateResult =
  | { ok: true; validation: FlowValidationResult; candidate: FlowVersionCandidate }
  | { ok: false; validation: FlowValidationResult };

/**
 * Produz um candidato imutável de versão. Ele NÃO publica por conta própria:
 * identidade, referência ativa, auditoria e recibo pertencem ao comando.
 */
export function compileFlowVersionCandidate(
  draft: FlowDraft,
  catalog: FlowDependencyCatalog,
  options: CompileFlowCandidateOptions,
): CompileFlowCandidateResult {
  const compiled = compileFlowDraft(draft, catalog, options);
  if (!compiled.ok) return { ok: false, validation: compiled.validation };
  const candidate: FlowVersionCandidate = {
    ownerId: draft.ownerId,
    flowId: draft.id,
    funnelId: draft.funnelId,
    structureType: draft.structureType,
    version: options.version,
    sourceRevision: draft.revision,
    snapshot: compiled.snapshot,
    plan: compiled.plan,
    hash: compiled.hash,
    dependencies: compiled.dependencies,
    changelog: options.changelog,
    authorId: options.authorId,
    createdAt: options.createdAt,
  };
  return { ok: true, validation: compiled.validation, candidate: deepFreeze(candidate) };
}

function deepFreeze<T>(value: T): T {
  if (value === null || typeof value !== "object" || Object.isFrozen(value)) return value;
  for (const child of Object.values(value as Record<string, unknown>)) deepFreeze(child);
  return Object.freeze(value) as T;
}
